// ── Shared numeric sanitizer + categorical detection ──────────────────────
// Used by every feature-selection/feature-engineering live-preview table
// (Variance Threshold, Correlation, Chi-Square, ANOVA, Skew Correction) so
// they all agree on what counts as "a number" and "a categorical column" —
// previously each file had its own ad-hoc `parseVal` (some stripping only
// `$,`, missing €£¥/%/whitespace) and Chi-Square/ANOVA guessed categorical
// columns from a fragile substring match on the column NAME (e.g. 'status',
// 'id', 'code'), which both under- and over-matched real columns.

// Mirrors the backend's `parse_num` (server/app/engine/helpers.py) exactly:
// strips currency symbols, thousands separators, whitespace, underscores,
// and a trailing percent sign before parsing — so a value the backend can
// parse never shows as unparseable in the frontend's live preview, and
// vice versa.
export function parseNumeric(v) {
  if (v === null || v === undefined) return null;
  let s = String(v).trim();
  s = s.replace(/[$€£¥,\s_]/g, '');
  s = s.replace(/%$/, '');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// Resolves the rows a feature-engineering inspector should compute its live
// preview from. `uploadedRows` (rows arriving at this step — see App.jsx's
// `beforeRows`) is trusted whenever it's an array, even an empty one, since
// that's a real answer ("no data yet"), not a missing one. The old fallback
// chain (`uploadedRows || pipeline?.inputFile?.rows || SOURCE_ROWS`) had a
// landmine: `pipeline.inputFile.rows` is sometimes a plain row-COUNT number
// (not an array — see the "N rows on file" placeholder state), and `!rows ||
// !rows.length` on a number silently returns an empty candidate list with
// no indication why. Only fall through to `pipeline.inputFile.rows`/
// `fallback` when `uploadedRows` itself was never passed at all (undefined).
export function resolveLiveRows(uploadedRows, pipeline, fallback = []) {
  if (Array.isArray(uploadedRows)) return uploadedRows;
  const stored = pipeline?.inputFile?.rows;
  if (Array.isArray(stored)) return stored;
  return fallback;
}

// Merges a block's fixed original-upload schema with the live per-step
// schema (the columns actually present in the rows arriving at this step,
// including anything an earlier step created) so a column selector can
// offer both. An original column stays selectable even after being
// transformed downstream (unchanged from before this merge existed); a
// column an earlier Scaling/Encoding/Outlier-Handling/etc. step created
// (e.g. `mrr_min_max_scaled`) becomes selectable too, so blocks can be
// chained. Original columns keep their existing order and come first; any
// live-only column not already in the original list is appended in live
// order.
export function mergedColumns(origColumns, liveColumns) {
  const orig = Array.isArray(origColumns) ? origColumns : [];
  const live = Array.isArray(liveColumns) ? liveColumns : [];
  const seen = new Set(orig.map(c => c.name));
  const merged = orig.slice();
  for (const c of live) {
    if (!seen.has(c.name)) {
      merged.push(c);
      seen.add(c.name);
    }
  }
  return merged;
}

// Mirrors the backend's Frequency Encoding near-unique/ID-like check
// (steps/feature_engineering/encoding.py's
// _FREQUENCY_UNIQUENESS_WARN_THRESHOLD) so a frontend, pre-run warning can
// reuse the exact same signal/threshold instead of inventing a new one.
export const NEAR_UNIQUE_RATIO_THRESHOLD = 0.9;

// Fraction of a column's non-blank values that are distinct, plus the raw
// counts behind it. Returns null when there's nothing to measure.
export function columnUniqueness(rows, col) {
  if (!rows || !rows.length || !col) return null;
  const vals = rows.map(r => r[col]).filter(v => v !== null && v !== undefined && v !== '');
  if (!vals.length) return null;
  const distinct = new Set(vals.map(String));
  return { ratio: distinct.size / vals.length, distinct: distinct.size, total: vals.length };
}

// Strict categorical/grouping check for a target/group variable (e.g.
// ANOVA's target). Unlike isCategoricalColumn below — which deliberately
// treats a low-cardinality NUMERIC column (a rating, a status code) as a
// legitimate categorical proxy for Chi-Square — a genuine grouping variable
// must be non-numeric: a numeric column like Age is always a continuous
// FEATURE candidate for ANOVA, never a target, no matter how few distinct
// values it has.
export function isGroupingColumn(col) {
  return !!col && col.type !== 'number';
}

// A column counts as categorical/discrete when most of its values aren't
// parseable numbers (text/category/boolean-like), OR when it IS numeric but
// low-cardinality relative to its row count (an encoded category, a rating,
// a status code stored as an integer). Matches the ratio convention
// `inferColumns` (fileIO.js) already uses for its own number/string split.
export function isCategoricalColumn(col, rows, sampleSize = 200) {
  if (!col || !rows || !rows.length) return col ? col.type !== 'number' : false;
  const sample = rows.slice(0, sampleSize);
  const distinct = new Set();
  let total = 0;
  let numeric = 0;
  for (const r of sample) {
    const v = r[col.name];
    if (v === null || v === undefined || v === '') continue;
    total++;
    distinct.add(String(v));
    if (parseNumeric(v) !== null) numeric++;
  }
  if (total === 0) return col.type !== 'number';
  const numericRatio = numeric / total;
  if (numericRatio <= 0.7) return true;
  return distinct.size <= 20 && distinct.size / total <= 0.5;
}
